import { useState, useRef, useEffect, Suspense, lazy, ComponentType } from "react";
import { DiagramDefinition } from "./types";
import { Maximize2, Minimize2, ZoomIn, ZoomOut } from "lucide-react";

interface DiagramShellProps {
  diagramId: string;
  definition: DiagramDefinition;
  lazyImport: () => Promise<{ default: ComponentType<{ definition: DiagramDefinition }> }>;
  caption?: string;
  className?: string;
}

export function DiagramShell({
  diagramId,
  definition,
  lazyImport,
  caption,
  className = "",
}: DiagramShellProps) {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  // Lazy load component via React.lazy once IntersectionObserver triggers
  const LazyDiagramComponent = useRef<ComponentType<{ definition: DiagramDefinition }> | null>(null);

  if (shouldLoad && !LazyDiagramComponent.current) {
    LazyDiagramComponent.current = lazy(lazyImport);
  }

  useEffect(() => {
    if (!containerRef.current || shouldLoad) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "500px 0px" }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [shouldLoad]);

  const Component = LazyDiagramComponent.current;

  return (
    <figure data-diagram-id={diagramId} className={`my-12 ${className}`}>
      {/* Container with reserved dimensions to eliminate CLS */}
      <div
        ref={containerRef}
        style={{ minHeight: "320px", aspectRatio: "16 / 9", contain: "layout paint" }}
        className="relative group overflow-hidden p-[2px] rounded-[1.5rem] bg-theme-surface border border-theme transition-colors hover:border-primary/40 shadow-sm w-full"
      >
        <div className="relative rounded-[calc(1.5rem-2px)] bg-theme-elevated p-4 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] h-full w-full flex flex-col justify-center items-center">
          {/* Controls Bar */}
          <div className="absolute top-4 right-4 z-10 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setIsExpanded(true)}
              className="p-2 rounded-full bg-theme-surface border border-theme text-theme-secondary hover:text-primary hover:bg-theme-elevated transition-colors shadow-sm"
              title="Expand Diagram"
            >
              <Maximize2 size={16} />
            </button>
          </div>

          {/* Accessibility Screen Reader Description Fallback */}
          <div className="sr-only" tabIndex={0} aria-label={`Diagram: ${definition.title}`}>
            <h3>{definition.title}</h3>
            <p>{definition.description}</p>
          </div>

          {/* Lazy Component Renderer */}
          {shouldLoad && Component ? (
            <Suspense
              fallback={
                <div className="flex flex-col h-full w-full items-center justify-center gap-3">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  <span className="text-xs font-mono text-theme-muted">Loading Diagram...</span>
                </div>
              }
            >
              <Component definition={definition} />
            </Suspense>
          ) : (
            <div className="flex flex-col h-full w-full items-center justify-center gap-3">
              <div className="h-8 w-8 rounded-full border border-theme bg-theme-surface animate-pulse" />
              <span className="text-xs font-mono text-theme-muted">Scroll to load diagram</span>
            </div>
          )}
        </div>
      </div>

      {caption && (
        <figcaption className="mt-4 text-center text-sm font-medium text-theme-muted uppercase tracking-wider">
          {caption}
        </figcaption>
      )}

      {/* Expanded Modal View */}
      {isExpanded && Component && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8">
          <div className="relative w-full max-w-6xl max-h-full h-[90vh] bg-theme-surface rounded-[2rem] border border-theme shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-theme bg-theme-elevated/50 backdrop-blur-md">
              <h3 className="font-heading font-semibold text-theme tracking-tight px-2">
                {definition.title}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setScale((s) => Math.max(s - 0.25, 0.5))}
                  className="p-2 rounded-full bg-theme-surface border border-theme text-theme-secondary hover:text-primary transition-colors"
                >
                  <ZoomOut size={18} />
                </button>
                <span className="text-sm font-mono text-theme-muted min-w-[3ch] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={() => setScale((s) => Math.min(s + 0.25, 3))}
                  className="p-2 rounded-full bg-theme-surface border border-theme text-theme-secondary hover:text-primary transition-colors"
                >
                  <ZoomIn size={18} />
                </button>
                <div className="w-px h-6 bg-theme mx-2" />
                <button
                  onClick={() => {
                    setIsExpanded(false);
                    setScale(1);
                  }}
                  className="p-2 rounded-full bg-theme-surface border border-theme text-theme-secondary hover:text-red-400 transition-colors"
                >
                  <Minimize2 size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 md:p-8 flex">
              <div 
                style={{ 
                  transform: `scale(${scale})`, 
                  transformOrigin: "top center", 
                  transition: "transform 0.2s" 
                }} 
                className="w-full m-auto flex justify-center min-h-min"
              >
                <Component definition={definition} />
              </div>
            </div>
          </div>
        </div>
      )}
    </figure>
  );
}
